import React from "react";
import { connect } from "react-redux";
import { BaseControl, ControlProps } from "./BaseControl";
import { QuestionModel } from "../../models/QuestionModel";
import { StoreState } from "../../models/states/StoreState";
import { LookupTablePage } from "../../models/LookupTablePage";
import {
    getSettingString,
    getSettingBoolean,
    getSettingNumber,
} from "../../models/utilities/settingUtilities";
import { LookupTableParams } from "../../models/LookupTableParams";
import { isNullOrEmpty } from "../../utils/index";
import { ValidationResultModel } from "../../models/ValidationResultModel";
import { ValidationBuilder } from "../../models/builders/ValidationBuilder";
import { Piping } from "../../piping";
import { ValidationOptionsModel } from "../../models/ValidationOptionsModel";
import { __getStore__ } from "../../redux/store";
import { AdvancedSettings, PaginatedTable, TableRow, TableTexts } from "../table/PaginatedTable";
import { isVisible } from "../../models/utilities/choiceUtilities";
import { getChoice } from "../../models/utilities/variableUtilities";
import { getSoftPromptCode } from "../../models/utilities/questionUtilities";
import { lookupTableFetch } from "../../redux/actions";
import { translate } from "../../models/utilities/translationUtilities";
import { isQuestionValidatedServerSide } from "../../models/utilities/validationUtilities";
import { OkResult } from "../shared/ModalDialog";
import { addChoiceItem } from "../../redux/thunks/lookuptable";
import { ThunkDispatchProp } from "../../models/actions/ThunkDispatchProp";

interface StateProps {
    page: LookupTablePage;
}

interface Props extends ControlProps, StateProps, ThunkDispatchProp {}

interface State {
    params: LookupTableParams;
    shouldDisplayFilters: boolean;
    prevPageStart: number;
}

const mapStateToProps = (state: StoreState, ownProps: Props): StateProps => ({
    page: state.lookupTable.lookupTable[ownProps.question.name],
});

const SEARCH = "SEARCH";
const SELECTED_ITEMS_LIMIT = "SELECTED_ITEMS_LIMIT";
const SELECTED_ITEMS = "SELECTED_ITEMS";
const DATA_TABLE_EMPTY_TABLE = "DATA_TABLE_EMPTY_TABLE";
const DATA_TABLE_INFO = "DATA_TABLE_INFO";
const DATA_TABLE_INFO_FILTERED = "DATA_TABLE_INFO_FILTERED";
const DATA_TABLE_LENGTH_MENU = "DATA_TABLE_LENGTH_MENU";
const LOOKUP_TABLE_SELECTED = "LookupTableSelected";
const LOOKUP_TABLE_UNSELECTED = "LookupTableUnselected";
const LOOKUP_TABLE_ALL = "LookupTableAll";
const LOOKUP_TABLE_ADD_CHOICE_BUTTON_LABEL = "LookupTableAddChoiceButtonLabel";
const LOOKUP_TABLE_CONFIRMATION_BUTTON_LABEL = "ConfirmationButtonLabel";
const LOOKUP_TABLE_CANCEL_BUTTON_LABEL = "CancelButtonLabel";
const LOOKUP_TABLE_ADD_CHOICE_POPUP_TITLE = "AddChoicePopupTitle";

class LookupTableImpl extends BaseControl<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            shouldDisplayFilters: false,
            params: {
                pageStart: 0,
                pageLength: 25,
                search: "",
                searchableColumns: [],
                columnFilters: {},
                sortColumn: "",
                sortAsc: false,
                selected: [],
                includeSelected: false,
                excludeSelected: false,
            },
            prevPageStart: 0,
        };
    }

    tableName = (props: Props) => getSettingString(props.question, "Name");

    pageCount = (props: Props) =>
        Math.ceil(props.page.totalFilteredRows / props.page.params.pageLength);

    currentPage = (props: Props) => props.page.params.pageStart / props.page.params.pageLength + 1;

    searchableColumns = (props: Props) =>
        props.sequencedVariables
            .filter((c) => getSettingBoolean(c, "Searchable"))
            .map((c) => getSettingString(c, "ColumnName"));

    showColumnFilters = (props: Props) => getSettingBoolean(props.question, "ColumnFilters");

    initializeQuestion(question: QuestionModel, props: Props): void {
        super.initializeQuestion(question, props);
        if (props.sequencedVariables.length === 0) {
            return;
        }
        const mentions = this.getSelectedInternalIds(props);
        const shouldDisplayFilters = mentions != null && mentions.length > 0;
        this.setState(
            (state) => {
                const newState = { ...state };
                newState.shouldDisplayFilters = shouldDisplayFilters;
                newState.params = {
                    pageStart: 0,
                    pageLength: getSettingNumber(props.question, "PageSize", 25),
                    selected: this.getSelectedInternalIds(props),
                    search: "",
                    searchableColumns: this.searchableColumns(props),
                    columnFilters: {},
                    sqlFilter: "",
                    sortColumn: getSettingString(question, "SortColumn"),
                    sortAsc: getSettingBoolean(props.question, "SortDirection", "asc"),
                    includeSelected: false,
                    excludeSelected: false,
                };
                return newState;
            },
            () => this.updateSqlFilter(question, props),
        );
    }

    willUpdateMentions(question: QuestionModel, nextProps: Props) {
        super.willUpdateMentions(question, nextProps);
        this.updateSqlFilter(question, nextProps);
        this.updateSelected(nextProps);
    }

    updateSelected(props: Props) {
        this.setState((state) => ({
            ...state,
            params: {
                ...state.params,
                selected: this.getSelectedInternalIds(props),
            },
        }));
    }

    getMaxAnswer(props: Props) {
        return getSettingNumber(props.question, "MaxAllowedAnswers");
    }

    getAdvancedSettings(props: Props): AdvancedSettings {
        return {
            respondentActions: {
                allow: getSettingBoolean(props.question, "AllowRespondentToAddCustomChoice"),
                access: getSettingNumber(props.question, props.inputAccess),
            },
            displayTabs: getSettingBoolean(props.question, "DisplayAllSelectedAndUnselectedTabs"),
            hideTableByDefault: getSettingBoolean(props.question, "HideTableByDefault"),
            hideTableHeader: getSettingBoolean(props.question, "HideTableHeader"),
            hideTablePageNavigation: getSettingBoolean(props.question, "HideTablePageNavigation"),
            version: getSettingString(props.question, "Version"),
            searchBarForAllColumns: getSettingBoolean(props.question, "SearchBarForAllColumns"),
        };
    }

    updateSqlFilter(question: QuestionModel, props: Props) {
        const { interview } = __getStore__().getState();
        let filter: string;
        if (question.lookupFilter) {
            filter = Piping.evaluateRecalls(interview, question.lookupFilter.expression);
        }
        if (filter == null) {
            this.fetchLookupTable(props);
            return;
        }
        this.setState(
            (state) => ({
                ...state,
                params: {
                    ...state.params,
                    sqlFilter: filter,
                },
            }),
            () => this.fetchLookupTable(props),
        );
    }

    fetchLookupTable(props: Props) {
        props.dispatch(
            lookupTableFetch(this.tableName(props), this.state.params, props.question.name),
        );
    }

    idForRow(row: number) {
        return `${this.props.question.name}_${row}`;
    }

    isVisibleColumn(props: Props, columnName: string) {
        return props.sequencedVariables.some(
            (variable) =>
                columnName === getSettingString(variable, "ColumnName") &&
                getSettingBoolean(variable, "Visible"),
        );
    }

    getColumnNameByIndex(page: LookupTablePage, index) {
        return page.columns[index];
    }

    getInternalIdVariableName(props: Props) {
        return props.question.variables.find((v) =>
            getSettingBoolean(v, "ColumnName", "InternalId"),
        ).name;
    }

    maxAnswersReached(props: Props) {
        const maxAnswer = this.getMaxAnswer(props);
        const mentionCount = this.getSelectedRowCount(props);
        return maxAnswer > 0 && mentionCount >= maxAnswer;
    }

    onChangeRowSelected = (row: TableRow, selected: boolean) => {
        const { props } = this;
        const maxAnswer = this.getMaxAnswer(props);
        if (selected && this.maxAnswersReached(props) && maxAnswer > 1) {
            return;
        }
        const isRowSelected = this.isRowSelected(props, row.id);
        if (isRowSelected && !selected) {
            const mentionIndex = this.getMentions(
                props,
                this.getInternalIdVariableName(props),
            ).findIndex((m) => parseInt(m.value, 10) === row.id);
            props.question.variables.forEach((variable) => {
                this.setMentions(props, variable.name, (mentions) =>
                    mentions.splice(mentionIndex, 1),
                );
            });
        } else if (!isRowSelected && selected) {
            props.question.variables.forEach((variable) => {
                const columnName = getSettingString(variable, "ColumnName");
                const value = row[columnName];
                if (maxAnswer > 1) {
                    this.setMentions(props, variable.name, (mentions) =>
                        mentions.add(String(value)),
                    );
                } else {
                    this.setMentions(props, variable.name, (mentions) =>
                        mentions.set(String(value)),
                    );
                }
            });
        }
    };

    onSelectedTabChange = (value: string) => {
        let { includeSelected, excludeSelected, pageStart } = this.state.params;
        let prevPageStart = this.state.prevPageStart;
        if (value === "all") {
            includeSelected = false;
            excludeSelected = false;
            pageStart = prevPageStart;
        }
        if (value === "selected") {
            includeSelected = true;
            excludeSelected = false;
            prevPageStart = pageStart;
            pageStart = 0;
        }
        if (value === "unselected") {
            includeSelected = false;
            excludeSelected = true;
            pageStart = prevPageStart;
        }
        this.setState(
            (state) => ({
                ...state,
                prevPageStart,
                params: {
                    ...state.params,
                    pageStart,
                    includeSelected,
                    excludeSelected,
                },
            }),
            () => this.fetchLookupTable(this.props),
        );
    };

    getSelectedRowCount(props: Props) {
        return this.getSelectedInternalIds(props).length;
    }

    getSelectedInternalIds(props: Props) {
        const mentions = this.getMentions(props, this.getInternalIdVariableName(props));
        return mentions.map((m) => parseInt(m.value, 10));
    }

    isRowSelected(props: Props, id: number) {
        return this.getSelectedInternalIds(props).includes(id);
    }

    changePage(page: number) {
        this.setState(
            (state) => ({
                ...state,
                params: {
                    ...state.params,
                    pageStart: (page - 1) * state.params.pageLength,
                },
            }),
            () => this.fetchLookupTable(this.props),
        );
    }

    onFirstPageClick = () => {
        this.changePage(1);
    };

    onLastPageClick = () => {
        this.changePage(this.pageCount(this.props));
    };

    onPrevPageclick = () => {
        const page = this.currentPage(this.props) - 1;
        if (page >= 1) {
            this.changePage(page);
        }
    };

    onNextPageClick = () => {
        const page = this.currentPage(this.props) + 1;
        if (page <= this.pageCount(this.props)) {
            this.changePage(page);
        }
    };

    onChangeItemCount = (event) => {
        const { value } = event.target.value;
        this.onFirstPageClick();
        this.setState(
            (state) => ({
                ...state,
                params: {
                    ...state.params,
                    pageLength: parseInt(value, 10),
                },
            }),
            () => this.fetchLookupTable(this.props),
        );
    };

    onChangeSearch = (value: string) => {
        this.setState(
            (state) => ({
                ...state,
                params: {
                    ...state.params,
                    search: value,
                    pageStart: 0,
                },
            }),
            () => this.fetchLookupTable(this.props),
        );
    };

    onClickHeader = (header: string) => {
        if (this.state.params.sortColumn.includes(header)) {
            this.setState(
                (state) => ({
                    ...state,
                    params: {
                        ...state.params,
                        sortAsc: !state.params.sortAsc,
                    },
                }),
                () => this.fetchLookupTable(this.props),
            );
        } else {
            this.setState(
                (state) => ({
                    ...state,
                    params: {
                        ...state.params,
                        sortColumn: header,
                        sortAsc: true,
                    },
                }),
                () => this.fetchLookupTable(this.props),
            );
        }
    };

    onClearColumnFilters = () => {
        this.setState(
            (state) => ({
                ...state,
                params: {
                    ...state.params,
                    columnFilters: {},
                    pageStart: 0,
                },
            }),
            () => this.fetchLookupTable(this.props),
        );
    };

    onChangeColumnFilter = (column: string, value: string) => {
        this.setState(
            (state) => {
                let columnFilters = {
                    ...state.params.columnFilters,
                    [column]: value,
                };
                columnFilters = Object.entries(columnFilters)
                    .filter(([, v]) => v)
                    .reduce((obj, [k, v]) => ({ ...obj, [k]: v }), {});
                return {
                    ...state,
                    params: {
                        ...state.params,
                        columnFilters,
                        pageStart: 0,
                    },
                };
            },
            () => this.fetchLookupTable(this.props),
        );
    };

    onFieldKeyPress = (event) => {
        if (event.key === "Enter") {
            this.fetchLookupTable(this.props);
        }
    };

    hasMentions(props: Props) {
        const variableName = this.getInternalIdVariableName(props);
        const mentions = this.getMentions(props, variableName);
        return (
            mentions !== undefined &&
            mentions.length > 0 &&
            mentions.some((m) => this.mentionIsValid(m, props.question))
        );
    }

    validate(options?: ValidationOptionsModel): ValidationResultModel {
        const { question, useClientValidation } = this.props;

        if (isQuestionValidatedServerSide(question.type, useClientValidation)) {
            return new ValidationBuilder().build();
        }

        const required = this.isAnswerRequired(this.props.questionnaire, this.props.question);
        const [variable] = question.variables;
        const min = this.minValue();
        if (this.hasMentions(this.props)) {
            const max = this.maxValue();
            const selected = this.state.params.selected.length;
            if (max != null && selected > Number(max)) {
                const message = getSettingString(question, "UpperBoundNotFulfilledMessage");
                return new ValidationBuilder()
                    .fail(
                        variable.name,
                        !isNullOrEmpty(message) ? message : "UpperBoundNotFulfilled",
                        { max: String(max) },
                    )
                    .build();
            }
            if (min != null && selected < Number(min)) {
                const message = getSettingString(question, "LowerBoundNotFulfilledMessage");
                return new ValidationBuilder()
                    .fail(variable.name, !isNullOrEmpty(message) ? message : "InvalidChoicesMin", {
                        min: String(min),
                    })
                    .build();
            }
        } else if (required) {
            // Validate soft-prompt.
            const validation = new ValidationBuilder();
            const softPromptCode = getSoftPromptCode(question);
            if (this.props.showSoftPrompt && softPromptCode != null) {
                const softPromptChoice = getChoice(question.variables[0], softPromptCode);
                if (!isVisible(softPromptChoice)) {
                    this.setMentions(this.props, variable.name, (mentions) =>
                        mentions.set(softPromptCode),
                    );
                }
                return new ValidationBuilder().build();
            }
            if (min != null && Number(min) > 0) {
                const message = getSettingString(question, "LowerBoundNotFulfilledMessage");
                validation.fail(
                    variable.name,
                    !isNullOrEmpty(message) ? message : "InvalidChoicesMin",
                    { min: String(min) },
                );
            }
            // Question is required but has no mentions.
            return validation.fail(variable.name, "IncompleteAnswer").build();
        }
        // Question is valid.
        return new ValidationBuilder().build();
    }

    hasSearchableColumns() {
        return this.props.sequencedVariables.some((c) => getSettingBoolean(c, "Searchable"));
    }

    onSortChanged = (sortColumn: string, sortAsc: boolean) => {
        this.setState(
            (state) => ({
                ...state,
                params: {
                    ...state.params,
                    sortColumn,
                    sortAsc,
                },
            }),
            () => this.fetchLookupTable(this.props),
        );
    };

    onPageChanged = (pageStart: number, pageLength: number) => {
        this.setState(
            (state) => ({
                ...state,
                params: {
                    ...state.params,
                    pageStart,
                    pageLength,
                },
            }),
            () => this.fetchLookupTable(this.props),
        );
    };

    getTableTexts = (question: QuestionModel, language: string): TableTexts => {
        const translateKey = (key: string, defaultValue: string) =>
            translate(question, key, language) || defaultValue;

        const translateGlobal = (key: string, defaultValue: string) =>
            translate(this.props.questionnaire, key, language) || defaultValue;

        return {
            search: translateKey(SEARCH, "Search"),
            selectedItemsLimit: translateKey(SELECTED_ITEMS_LIMIT, "{count} of {limit} selected"),
            selectedItems: translateKey(SELECTED_ITEMS, "{count} selected"),
            emptyTable: translateKey(DATA_TABLE_EMPTY_TABLE, "No items to display"),
            info: translateKey(DATA_TABLE_INFO, "Displaying _START_ to _END_ of _TOTAL_ item(s)"),
            infoFiltered: translateKey(DATA_TABLE_INFO_FILTERED, "(Filtered from _MAX_ item(s))"),
            lengthMenu: translateKey(DATA_TABLE_LENGTH_MENU, "Display _MENU_ items"),
            lookupTableAll: translateKey(LOOKUP_TABLE_ALL, "All"),
            lookupTableSelected: translateKey(LOOKUP_TABLE_SELECTED, "Selected"),
            lookupTableUnselected: translateKey(LOOKUP_TABLE_UNSELECTED, "Unselected"),
            addChoicePopupTitle: translateKey(LOOKUP_TABLE_ADD_CHOICE_POPUP_TITLE, "Add a choice"),
            confirmationButtonLabel: translateKey(
                LOOKUP_TABLE_CONFIRMATION_BUTTON_LABEL,
                "Confirm",
            ),
            cancelButtonLabel: translateKey(LOOKUP_TABLE_CANCEL_BUTTON_LABEL, "Cancel"),
            lookupTableAddChoiceButtonLabel: translateKey(
                LOOKUP_TABLE_ADD_CHOICE_BUTTON_LABEL,
                "New Choice",
            ),
            cancelNewItemText: translateGlobal("CancelButton", "Cancel"),
            saveNewItemText: translateGlobal("SubmitButton", "Submit"),
        };
    };

    onItemAdd = (formData: Record<string, string>): OkResult => {
        this.props
            .dispatch(addChoiceItem(this.props.question.name, this.tableName(this.props), formData))
            .then((newId: number) => {
                this.onChangeRowSelected({ id: newId, InternalId: newId, ...formData }, true);
            });
        this.fetchLookupTable(this.props);
        return {
            isSuccess: true,
        };
    };

    renderQuestion(question: QuestionModel) {
        const { props } = this;
        const { page, language } = props;
        if (!page) {
            return <div>Loading...</div>;
        }
        const { columns, rows, totalRows, totalFilteredRows, params, columnsTranslation } = page;
        const filterable = this.showColumnFilters(props);
        const columnFilters = params.columnFilters || {};
        const tableColumns = props.sequencedVariables
            .filter((variable) => {
                const columnName = getSettingString(variable, "ColumnName");
                return this.isVisibleColumn(props, columnName) && columns.includes(columnName);
            })
            .map((variable) => {
                const columnName = getSettingString(variable, "ColumnName");
                const translatedName = translate(
                    variable,
                    "TEXT",
                    language,
                    (columnsTranslation || {})[columnName],
                );
                return {
                    name: columnName,
                    searchable: getSettingBoolean(variable, "Searchable"),
                    width: getSettingNumber(variable, "Width"),
                    filter: columnFilters[columnName],
                    translatedName,
                };
            })
            .sort((a, b) => columns.indexOf(a.name) - columns.indexOf(b.name));
        const pageRows = rows.map(({ RowNum, ...row }) => {
            const selected = this.isRowSelected(props, parseInt(row.InternalId, 10));
            return {
                id: parseInt(row.InternalId, 10),
                index: RowNum,
                selected,
                ...row,
            };
        });

        const max = this.getMaxAnswer(props);
        const totalSelected = this.getSelectedRowCount(props);
        const advancedSettings = this.getAdvancedSettings(props);
        const isAddItem =
            advancedSettings.respondentActions.allow &&
            advancedSettings.respondentActions.access > 0 &&
            (!max || max > totalSelected);

        return (
            <PaginatedTable
                id={question.elementId}
                pageStart={params.pageStart}
                columns={tableColumns}
                pageRows={pageRows}
                totalRows={totalRows}
                totalFilteredRows={totalFilteredRows}
                totalSelected={totalSelected}
                max={max}
                sortAsc={params.sortAsc}
                sortColumn={params.sortColumn}
                onChangePage={this.onPageChanged}
                onChangeSort={this.onSortChanged}
                selectable
                filterable={filterable}
                search={params.search || ""}
                texts={this.getTableTexts(question, language)}
                onChangeColumnFilter={this.onChangeColumnFilter}
                onChangeSearch={this.onChangeSearch}
                onChangeRowSelected={this.onChangeRowSelected}
                rowsPerPageOptions={[10, 25, 50, 100]}
                defaultRowsPerPage={params.pageLength}
                advancedSettings={advancedSettings}
                onSelectedTabChange={this.onSelectedTabChange}
                isAddNewItem={isAddItem}
                isShowPageInfo={false}
                isShowRowsPerPage={false}
                onItemAdd={this.onItemAdd}
            />
        );
    }
}

export const LookupTableControl = connect(mapStateToProps)(LookupTableImpl);
